import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, AlertTriangle, Search, Plus, XCircle, Building2, FileText, Pencil } from 'lucide-react';
import AppPageHeader from '../shell/AppPageHeader';
import { useFirmMainHeaderSlot } from './FirmMainHeaderContext';
import { useFirmCompanies } from './FirmCompaniesContext';
import { closingDateVariant, CLOSING_DATE_COLORS } from '../../utils/closingDate';
import {
  DEAL_ROLE_OPTIONS,
  DEAL_STRUCTURE_OPTIONS,
  LISTING_STATUS_OPTIONS,
  WORK_STATUS_OPTIONS,
  dealContextSummary,
  workStatusBadgeClasses,
  workStatusLabel,
} from './companyIntakeFields';
import { shellMainPaddingX } from '../../utils/shellLayoutClasses';
import { clientDataPanelClass, clientEmptyStateCardClass } from '../../utils/clientChromeClasses';
import TableRowKebabMenu from '../shared/TableRowKebabMenu';

export default function Companies() {
  const navigate = useNavigate();
  const { companies: rows, addCompany, loading, error, refreshCompanies } = useFirmCompanies();
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [closingDate, setClosingDate] = useState('');
  const [dealStructure, setDealStructure] = useState('unsure');
  const [dealRole, setDealRole] = useState('unsure');
  const [listingStatus, setListingStatus] = useState('unsure');
  const [workStatus, setWorkStatus] = useState('intake');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState(null as string | null);
  const [currentPage, setCurrentPage] = useState(1);
  const [kebabForCompanyId, setKebabForCompanyId] = useState(null as string | null);
  const pageSize = 10;

  const searchTrim = search.trim();
  const searchLower = searchTrim.toLowerCase();
  const filtered = searchTrim
    ? rows.filter((a) => a.companyName.toLowerCase().includes(searchLower) || a.closingDate.includes(searchTrim))
    : rows;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const totalAnalyses = rows.reduce((s, a) => s + a.analysesCount, 0);
  const totalOverThreshold = rows.reduce((s, a) => s + a.overThresholdCount, 0);
  const summaryBadges = [
    { label: 'Companies', value: String(rows.length) },
    { label: 'Total 280G analyses', value: String(totalAnalyses) },
    { label: 'Over threshold', value: String(totalOverThreshold) },
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [search, rows.length]);

  const openAdd = () => {
    setFormError(null);
    setCompanyName('');
    setClosingDate('');
    setDealStructure('unsure');
    setDealRole('unsure');
    setListingStatus('unsure');
    setWorkStatus('intake');
    setNotes('');
    setAddOpen(true);
  };

  const closeAdd = () => {
    setAddOpen(false);
    setFormError(null);
  };

  const submitAdd = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const name = companyName.trim();
    const close = closingDate.trim();
    if (!name) {
      setFormError('Company name is required.');
      return;
    }
    if (!close) {
      setFormError('Closing date is required.');
      return;
    }
    try {
      const created = await addCompany({
        companyName: name,
        closingDate: close,
        dealStructure,
        dealRole,
        listingStatus,
        workStatus,
        notes: notes.trim(),
      });
      closeAdd();
      navigate(`/firm/companies/${created.id}`);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not add company.');
    }
  };

  const firmHeader = useMemo(() => <AppPageHeader variant="firmShell" title="Companies" />, []);
  useFirmMainHeaderSlot(firmHeader);

  return (
    <>
      <main className="flex-1 flex flex-col min-h-0 min-w-0">
        <div className={`flex-1 overflow-auto pb-8 pt-3 sm:pt-4 ${shellMainPaddingX}`}>
          <div className="w-full space-y-6 relative">
            {error ? (
              <div
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                role="alert"
              >
                <span>{error}</span>
                <button
                  type="button"
                  onClick={() => refreshCompanies()}
                  className="shrink-0 px-3 py-1.5 rounded-md border border-red-300 text-red-900 text-xs font-medium hover:bg-red-100"
                >
                  Retry
                </button>
              </div>
            ) : null}
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <p className="text-sm text-gray-500">
                Add companies and open each workspace to run Section 280G analyses, track deal intake, and manage client
                logins.
              </p>
              <div className="flex flex-wrap items-center gap-2 justify-end ml-auto">
                {summaryBadges.map(({ label, value }) => (
                  <span
                    key={label}
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-50 border border-emerald-100/80 text-emerald-800 shadow-sm"
                  >
                    <span className="text-emerald-700 mr-1.5">{label}:</span>
                    <span className="font-medium text-emerald-900 tabular-nums">{value}</span>
                  </span>
                ))}
              </div>
            </div>

            {loading && rows.length === 0 ? (
              <div className={`${clientEmptyStateCardClass} text-sm text-gray-500`}>Loading companies…</div>
            ) : !loading && !error && rows.length === 0 ? (
              <div className={clientEmptyStateCardClass}>
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <FileText size={24} className="text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">No companies yet</p>
                <p className="text-sm text-gray-500 mt-1 mb-6">
                  Add a company to run and track Section 280G analyses for that deal.
                </p>
                <button
                  type="button"
                  onClick={openAdd}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg btn-primary text-sm font-medium focus:ring-2 focus:ring-emerald-600/50 focus:ring-offset-2"
                >
                  <Plus size={18} />
                  Add company
                </button>
              </div>
            ) : rows.length > 0 ? (
              <div className={clientDataPanelClass}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-5 py-4 border-b border-gray-200 bg-gray-50/80">
                  <div className="relative w-full sm:flex-1 sm:min-w-0 sm:max-w-md">
                    <Search
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search by company or closing date…"
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none"
                      aria-label="Search companies"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={openAdd}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg btn-primary text-sm font-medium transition-colors shrink-0 focus:ring-2 focus:ring-emerald-600/50 focus:ring-offset-2"
                  >
                    <Plus size={18} />
                    Add company
                  </button>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Closing date
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Analysis runs
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Over threshold
                      </th>
                      <th className="px-5 py-3 text-right w-16">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-8 text-center text-sm text-gray-500">
                          No companies match your search.
                        </td>
                      </tr>
                    ) : (
                      paginated.map((a) => {
                        const closingVariant = closingDateVariant(a.closingDate);
                        return (
                          <tr key={a.id} className="hover:bg-gray-50/80">
                            <td className="px-5 py-4">
                              <Link
                                to={`/firm/companies/${a.id}`}
                                className="font-medium text-emerald-700 hover:text-emerald-900 hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:ring-offset-0 rounded-sm"
                              >
                                {a.companyName}
                              </Link>
                              {dealContextSummary(a) ? (
                                <span className="block text-xs text-gray-500 mt-0.5">{dealContextSummary(a)}</span>
                              ) : null}
                            </td>
                            <td className="px-5 py-4 text-sm">
                              <span
                                className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${workStatusBadgeClasses(a.workStatus)}`}
                              >
                                {workStatusLabel(a.workStatus)}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-sm">
                              <span className={CLOSING_DATE_COLORS[closingVariant]}>{a.closingDate}</span>
                            </td>
                            <td className="px-5 py-4 text-sm text-gray-600 tabular-nums">{a.analysesCount}</td>
                            <td className="px-5 py-4 text-sm text-gray-600">
                              {a.overThresholdCount > 0 ? (
                                <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                                  <AlertTriangle size={14} className="shrink-0" aria-hidden />
                                  {a.overThresholdCount}
                                </span>
                              ) : (
                                <span className="text-gray-500">0</span>
                              )}
                            </td>
                            <td className="px-5 py-4 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(`/firm/companies/${a.id}`, {
                                      state: { openCompanyEdit: true },
                                    })
                                  }
                                  className="hidden sm:inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200/90 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/45 focus-visible:ring-offset-1"
                                  title="Edit intake and deal details"
                                  aria-label="Edit company"
                                >
                                  <Pencil size={18} strokeWidth={2} aria-hidden />
                                </button>
                                <TableRowKebabMenu
                                  ariaLabel={`Actions for ${a.companyName}`}
                                  isOpen={kebabForCompanyId === a.id}
                                  onOpenChange={(open) => setKebabForCompanyId(open ? a.id : null)}
                                  entries={[
                                    {
                                      type: 'item',
                                      id: 'open',
                                      label: 'Open company',
                                      description: 'View workspace & analyses',
                                      icon: (
                                        <ChevronRight
                                          size={16}
                                          strokeWidth={2}
                                          className="text-emerald-700"
                                          aria-hidden
                                        />
                                      ),
                                      onClick: () => navigate(`/firm/companies/${a.id}`),
                                      tone: 'default',
                                    },
                                    {
                                      type: 'item',
                                      id: 'edit',
                                      label: 'Edit company',
                                      description: 'Intake & deal details',
                                      icon: (
                                        <Pencil size={16} strokeWidth={2} className="text-emerald-700" aria-hidden />
                                      ),
                                      onClick: () =>
                                        navigate(`/firm/companies/${a.id}`, {
                                          state: { openCompanyEdit: true },
                                        }),
                                      tone: 'default',
                                    },
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                {filtered.length > pageSize ? (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
                    <div>
                      Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * pageSize, filtered.length)}</span> of{' '}
                      <span className="font-medium">{filtered.length}</span> companies
                      {searchTrim ? (
                        <>
                          {' '}
                          (<span className="font-medium">{rows.length}</span> total)
                        </>
                      ) : null}
                    </div>
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 border border-gray-300 rounded-l-md text-xs font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1.5 text-xs border-y border-gray-300 bg-white">
                        Page <span className="font-medium">{currentPage}</span> of{' '}
                        <span className="font-medium">{totalPages}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 border border-gray-300 rounded-r-md text-xs font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </main>

      <div
        className={`fixed inset-0 z-50 transition-opacity duration-200 ${addOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-0 bg-black/40 transition-opacity duration-200" onClick={closeAdd} aria-hidden />
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-lg bg-white shadow-xl flex flex-col transition-transform duration-300 ease-out ${
            addOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <form className="flex flex-col flex-1 min-h-0" onSubmit={submitAdd}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0 gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                  <Building2 size={20} aria-hidden />
                </span>
                <h3 className="font-medium text-gray-900">Add company</h3>
              </div>
              <button
                type="button"
                onClick={closeAdd}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg shrink-0"
                aria-label="Close"
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              <p className="text-sm text-gray-600 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5">
                Deal structure, role, and listing status help determine what to capture next—follow-up questions,
                documents, and analysis steps can follow from this profile.
              </p>
              {formError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  {formError}
                </div>
              ) : null}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="firm-add-company">
                  Company name
                </label>
                <input
                  id="firm-add-company"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Holdings Inc."
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600 outline-none"
                  autoComplete="organization"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="firm-add-closing">
                  Closing date
                </label>
                <input
                  id="firm-add-closing"
                  type="date"
                  value={closingDate}
                  onChange={(e) => setClosingDate(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="firm-add-structure">
                  Deal structure
                </label>
                <select
                  id="firm-add-structure"
                  value={dealStructure}
                  onChange={(e) => setDealStructure(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600 outline-none"
                >
                  {DEAL_STRUCTURE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="firm-add-role">
                  Company&apos;s role in the deal
                </label>
                <select
                  id="firm-add-role"
                  value={dealRole}
                  onChange={(e) => setDealRole(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600 outline-none"
                >
                  {DEAL_ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="firm-add-listing">
                  Listing
                </label>
                <select
                  id="firm-add-listing"
                  value={listingStatus}
                  onChange={(e) => setListingStatus(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600 outline-none"
                >
                  {LISTING_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="firm-add-work-status">
                  Overall status
                </label>
                <p className="text-xs text-gray-500 mb-1.5">
                  Deal / 280G workflow for this company (separate from client user active/inactive).
                </p>
                <select
                  id="firm-add-work-status"
                  value={workStatus}
                  onChange={(e) => setWorkStatus(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600 outline-none"
                >
                  {WORK_STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="firm-add-notes">
                  Notes <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  id="firm-add-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Anything that affects scope or what to collect next…"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600 outline-none resize-y min-h-[5rem]"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={closeAdd}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 rounded-lg btn-primary text-sm font-medium">
                Add company
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
